import { useState, useEffect } from "react";
import { FiSettings, FiPlus, FiTrash2, FiMail, FiCheck, FiInfo, FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { updatePasswordAPI } from "../api/auth";

export default function Settings() {
  const { user, token } = useAuth();
  const [testEmails, setTestEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");

  // Update password state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  // Load saved test emails from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("saved_test_emails");
    if (saved) {
      try {
        setTestEmails(JSON.parse(saved));
      } catch (e) {
        setTestEmails([user?.email].filter(Boolean));
      }
    } else if (user?.email) {
      setTestEmails([user.email]);
      localStorage.setItem("saved_test_emails", JSON.stringify([user.email]));
    }
  }, [user]);

  // Add a new test email
  const handleAddEmail = (e) => {
    e.preventDefault();
    const emailToAdd = newEmail.trim().toLowerCase();
    if (!emailToAdd) return;

    if (!emailToAdd.includes("@") || !emailToAdd.includes(".")) {
      toast.error("Please enter a valid email address!");
      return;
    }

    if (testEmails.includes(emailToAdd)) {
      toast.error("This email is already in your test list!");
      return;
    }

    const updated = [...testEmails, emailToAdd];
    setTestEmails(updated);
    localStorage.setItem("saved_test_emails", JSON.stringify(updated));
    setNewEmail("");
    toast.success("Saved test email added!");
  };

  // Remove a test email
  const handleRemoveEmail = (emailToRemove) => {
    const updated = testEmails.filter((email) => email !== emailToRemove);
    setTestEmails(updated);
    localStorage.setItem("saved_test_emails", JSON.stringify(updated));
    toast.success("Test email removed!");
  };

  // Update Password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      toast.error("Please enter a new password.");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match.");
      return;
    }

    const accessToken = token?.access || localStorage.getItem("accessToken");
    if (!accessToken) {
      toast.error("Authentication token missing. Please log in again.");
      return;
    }

    setUpdatingPassword(true);
    try {
      await updatePasswordAPI(accessToken, newPassword);
      toast.success("Password updated successfully!");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.message || "Failed to update password.");
    } finally {
      setUpdatingPassword(false);
    }
  };


  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <FiSettings className="w-7 h-7 text-gray-700" />
        <h1 className="text-2xl font-bold text-gray-900">Application Settings</h1>
      </div>

      {/* SECTION 1: SAVED TEST EMAILS */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiMail className="text-blue-600" /> Saved Test Emails
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage your preset test email addresses so you don't have to type them every time you send a test email.
          </p>
        </div>

        {/* Add Email Form */}
        <form onSubmit={handleAddEmail} className="flex gap-3">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Enter a testing email (e.g. test@gmail.com)"
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 transition"
          >
            <FiPlus size={18} /> Add Test Email
          </button>
        </form>

        {/* List of Test Emails */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Your Preset Test Emails ({testEmails.length})
          </label>

          {testEmails.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No saved test emails yet. Add one above!</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {testEmails.map((email, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between bg-gray-50 border border-gray-200 p-3 rounded-lg hover:border-blue-300 transition"
                >
                  <div className="flex items-center gap-2.5 overflow-hidden">
                    <FiCheck className="text-green-600 shrink-0" size={16} />
                    <span className="text-sm font-medium text-gray-800 truncate">{email}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    className="p-1.5 text-gray-400 hover:text-red-600 transition rounded-md hover:bg-red-50"
                    title="Remove test email"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* SECTION: UPDATE PASSWORD */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiLock className="text-blue-600" /> Update Password
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Update your account password below. Enter your new password and confirm it.
          </p>
        </div>

        <form onSubmit={handleUpdatePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={updatingPassword}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 transition disabled:opacity-50"
          >
            <FiLock size={16} />
            {updatingPassword ? "Updating Password..." : "Update Password"}
          </button>
        </form>
      </div>

      {/* SECTION 2: EMAIL PROVIDER GUIDELINES */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FiInfo className="text-blue-600" /> Email Provider Setup Guidelines
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          {/* AWS SES Card */}
          <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-lg">
            <h3 className="font-bold text-blue-900 text-sm mb-1">AWS SES Mode</h3>
            <p className="text-xs text-blue-800 leading-relaxed">
              Default high-volume email sender. Uses <code>AWS_ACCESS_KEY_ID</code> and <code>AWS_SECRET_ACCESS_KEY</code> configured in backend <code>.env</code>.
            </p>
          </div>

          {/* Gmail SMTP Card */}
          <div className="bg-emerald-50/50 border border-emerald-100 p-4 rounded-lg">
            <h3 className="font-bold text-emerald-900 text-sm mb-1">Gmail SMTP Mode</h3>
            <p className="text-xs text-emerald-800 leading-relaxed">
              To send via Gmail, set <code>EMAIL_PROVIDER=SMTP</code> and use a Gmail <strong>App Password</strong> in <code>SMTP_HOST_PASSWORD</code> in backend <code>.env</code>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

